import { MACHINE_SERVICE_TYPES, POS_TYPE } from '../../../common/constants';
import { addressToString } from '../../../common/utils/address';
import { getFullMachineName } from '../../../common/utils/machine';
import { getDocketAccessRequirementLabels } from '../../../common/utils/myDay/driverDaySteps';
import { getPickListStatusLabel } from '../../../common/utils/pickList/getPickListStatusLabel';
import { calculateTicketCount } from '../../../common/utils/serviceRuns/calculateTicketCount';
import { getServiceTypeName } from '../../../common/utils/serviceRuns/getServiceTypeName';
import { countDriverResolvedTickets } from '../../../common/utils/ticketList/countDriverTickets';
import { formatTemperature } from '../../../common/utils/utils';
import { analyseCoinMech } from '../../../common/utils/vending-utils';
import { RESTOCK_TYPE_LABELS } from '../servicerun/ServiceRunDetailMachineListItem';

// Presentation of one stop (service docket) for the run list, the stop screen
// and the route map: place first, then position, the classic run-list labels
// (SPEC decision 7) and the site requirement warnings (SPEC decision 8).

/** Site (location) name, e.g. "Sunshine Hospital". */
export const getStopSiteName = docket => docket?.machine?.location?.name || '';

/** Position at the site (place), e.g. "Level 2 · Lifts 9 & 10 (Left)"; falls back to the full machine name. */
export const getStopPositionName = docket => (
	docket?.machine?.place?.name ||
	getFullMachineName(docket?.machine, docket?.machine?.place, docket?.machine?.location) ||
	'Unnamed stop'
);

export const getStopAddress = docket => addressToString(docket?.machine?.location?.addressObj) || '';

// Consecutive stops at the same site form one group; a site visited again
// later in the route gets its own group so the list keeps the route order.
const getSiteKey = docket => docket?.machine?.location?.id || docket?.machine?.location?.name || `stop-${docket?.id}`;

/**
 * Groups route-ordered dockets by site: [{ key, siteKey, location, dockets: [{ docket, number }] }],
 * `number` being the 1-based stop number in the whole route.
 */
export const groupStopsBySite = (dockets) => {
	const groups = [];
	(dockets || []).forEach((docket, index) => {
		const siteKey = getSiteKey(docket);
		const lastGroup = groups[groups.length - 1];
		if (lastGroup && lastGroup.siteKey === siteKey) {
			lastGroup.dockets.push({ docket, number: index + 1 });
			return;
		}

		groups.push({
			key: `${siteKey}-${index}`,
			siteKey,
			location: docket?.machine?.location || null,
			dockets: [{ docket, number: index + 1 }],
		});
	});

	return groups;
};

const getWarningIcon = (label) => {
	if (/sign-in/i.test(label)) {
		return 'clipboard';
	}
	if (/access pass/i.test(label)) {
		return 'credit-card';
	}
	if (/PPE/.test(label)) {
		return 'shield';
	}

	return 'alert-triangle';
};

const toWarnings = labels => labels.map(text => ({ text, icon: getWarningIcon(text) }));
// Notes often already start with "Important" (or "IMPORTANT:"); those keep their own wording.
const toNoteWarnings = notes => (notes || [])
	.filter(note => note?.description)
	.map(note => ({
		text: /^\s*important\b/i.test(note.description) ? note.description : `Important: ${note.description}`,
		icon: 'info',
	}));

const getSiteRequirementWarnings = location => (location
	? toWarnings(getDocketAccessRequirementLabels({ machine: { location } }))
	: []);

/**
 * Site-level warnings (sign-in, location access pass, location PPE) for the
 * site group header. Machine keys are not site requirements, so they stay on
 * the stop's secondary line.
 */
export const getSiteWarnings = getSiteRequirementWarnings;

/** Important location notes, shown in red under the site warnings. */
export const getSiteImportantNotes = location => toNoteWarnings(location?.importantNotes);

/** Position-level warnings (POS access pass, POS PPE), attached to the stop row. */
export const getPositionWarnings = docket => (docket?.machine?.place
	? toWarnings(getDocketAccessRequirementLabels({ machine: { place: docket.machine.place } }))
	: []);

/** Site and position requirements, for the stop screen's warning card. */
export const getStopWarnings = docket => [
	...getSiteRequirementWarnings(docket?.machine?.location),
	...getPositionWarnings(docket),
];

/** Important location and machine notes, for the stop screen's red card under the warnings. */
export const getStopImportantNotes = docket => [
	...getSiteImportantNotes(docket?.machine?.location),
	...toNoteWarnings(docket?.machine?.importantNotes),
];

const isMarket = docket => docket?.machine?.type === POS_TYPE.MARKET;
const isMachineOffline = docket => docket?.machine?.isOffline ?? !docket?.machine?.commDevices?.length;

/** Service type with the restock type, e.g. "Restock & repair" / "All products" (classic labels). */
export const getStopServiceLabels = (docket, tenantSettings) => {
	const ticketCount = calculateTicketCount(docket, tenantSettings);
	const restockTypeLabel = docket?.machineServiceType !== MACHINE_SERVICE_TYPES.REPAIR
		? RESTOCK_TYPE_LABELS[docket?.machineRestockType]
		: null;

	return [getServiceTypeName(docket || {}, ticketCount), restockTypeLabel].filter(Boolean);
};

const isTemperatureOutOfRange = (temperature, tenantSettings) => {
	const [minThreshold, maxThreshold] = tenantSettings?.temperatureThresholds || [];
	if (temperature === null || temperature === undefined || minThreshold === undefined || maxThreshold === undefined) {
		return false;
	}
	const degrees = temperature / 100;

	return degrees < minThreshold || degrees > maxThreshold;
};

/**
 * The classic run-list labels of ServiceRunDetailMachineListItem, as Tag props:
 * pick status, tickets resolved/total, photo, offline, collect cash, coins
 * required, tub, cash bag, and a temperature alert when the machine reports a
 * temperature outside the tenant thresholds. `includeStepLabels: false` leaves
 * out tickets, pick status and photo (the stop screen shows them as steps);
 * `includeCashBag` / `includeTub: false` leave out values the screen shows as rows.
 */
export const getStopLabels = (docket, tenantSettings, { includeStepLabels = true, includeCashBag = true, includeTub = true } = {}) => {
	if (!docket?.machine) {
		return [];
	}

	const labels = [];
	const pickStatus = getPickListStatusLabel({
		inventoryPicked: docket.inventoryPicked,
		inventoryPartiallyPicked: docket.inventoryPartiallyPicked,
		isAnyItemPicked: docket.pickList?.isAnyItemPicked,
	});
	if (includeStepLabels && pickStatus !== 'Pending') {
		labels.push({ label: pickStatus, tone: docket.inventoryPicked ? 'success' : 'warning' });
	}

	const ticketCount = includeStepLabels && calculateTicketCount(docket, tenantSettings);
	if (ticketCount) {
		const resolvedCount = countDriverResolvedTickets(docket.ticketList.driverItems, tenantSettings);
		labels.push({
			label: `Tickets ${resolvedCount}/${ticketCount}`,
			tone: resolvedCount === ticketCount ? 'success' : 'neutral',
		});
	}

	if (includeStepLabels && docket.requirePhoto) {
		labels.push({ label: 'Photo', tone: docket.photoTaken ? 'success' : 'neutral' });
	}

	if (docket.machine.isOffline) {
		labels.push({ label: 'Offline' });
	}

	if (docket.collectCash) {
		labels.push({ label: 'Collect cash' });
	}

	if (docket.machine.hasCoinMech && !docket.machine.isOffline && docket.machine.coinMechDetail) {
		const { mechRequiresFill } = analyseCoinMech(docket.machine.coinMechDetail.tubesDetail, docket.machine.coinMech, tenantSettings, docket.machine);
		if (mechRequiresFill) {
			labels.push({ label: 'Coins required', tone: 'warning' });
		}
	}

	if (includeTub && tenantSettings?.enableTubsFieldOnDockets && docket.tub) {
		labels.push({ label: `Tub: ${docket.tub}` });
	}

	if (includeCashBag && docket.cashBag) {
		labels.push({ label: `Cash bag: ${docket.cashBag}` });
	}

	if (!isMachineOffline(docket) && isTemperatureOutOfRange(docket.machine.temperatureCelcius, tenantSettings)) {
		labels.push({ label: `Temperature ${formatTemperature(docket.machine.temperatureCelcius)}`, tone: 'danger' });
	}

	return labels;
};

/** Machine number plus model / wrap / type and a non-market key, e.g. "Black Combo · Key 8888". */
export const getStopMachineDetails = (docket) => {
	const machine = docket?.machine;
	if (!machine) {
		return '';
	}

	return [
		machine.machineModel?.name,
		machine.wrap?.name,
		machine.type === POS_TYPE.MACHINE ? machine.machineType?.name : null,
		!isMarket(docket) && machine.machineKey?.name ? `Key ${machine.machineKey.name}` : null,
	].filter(Boolean).join(' · ');
};

const SYNC_ENTRY_NOUNS = {
	createTicket: ['ticket', 'tickets'],
	updateTicketPhotos: ['ticket photo', 'ticket photos'],
	manualRestock: ['restock', 'restocks'],
	confirmStockLevels: ['stock confirmation', 'stock confirmations'],
	confirmMachineValues: ['values confirmation', 'values confirmations'],
	createMedia: ['photo', 'photos'],
	markServiceDocketCouldNotComplete: ['couldn\'t complete mark', 'couldn\'t complete marks'],
	clearServiceDocketCouldNotComplete: ['couldn\'t complete undo', 'couldn\'t complete undos'],
};

const isQueueEntryForStop = (entry, docket, ticketIds) => {
	const meta = entry.meta || {};
	switch (entry.configKey) {
		case 'createTicket':
			return meta.docketId === docket.id;
		case 'updateTicketPhotos':
			return ticketIds.has(meta.ticketId);
		case 'createMedia':
			return meta.serviceDocketId === docket.id || (Boolean(docket.machine?.id) && meta.machineId === docket.machine.id);
		default:
			return Boolean(SYNC_ENTRY_NOUNS[entry.configKey]) && meta.serviceDocketId === docket.id;
	}
};

const describeEntries = (entries) => {
	const counts = new Map();
	entries.forEach(entry => counts.set(entry.configKey, (counts.get(entry.configKey) || 0) + 1));

	return [...counts.entries()].map(([configKey, count]) => {
		const [singular, plural] = SYNC_ENTRY_NOUNS[configKey];
		return count === 1 ? singular : `${count} ${plural}`;
	});
};

/**
 * This stop's updates waiting in the offline queue (same matching as the
 * classic stop screen's pending sync banner): { pending, failed },
 * pending/failed being short descriptions such as "stock confirmation", "2 photos".
 */
export const getStopSyncState = (queue, docket) => {
	if (!docket?.id || !queue?.length) {
		return { pending: [], failed: [] };
	}

	const ticketIds = new Set((docket.ticketList?.driverItems || []).map(item => item.ticket?.id || item.id).filter(Boolean));
	const entries = queue.filter(entry => isQueueEntryForStop(entry, docket, ticketIds));

	return {
		pending: describeEntries(entries.filter(entry => !entry.failureCount)),
		failed: describeEntries(entries.filter(entry => entry.failureCount > 0)),
	};
};
